import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import './styles.css';

export default function Portfolio() {
  const [activeFilter, setActiveFilter] = useState('all');

  const projects = [
    {
      id: 1,
      title: "E-Commerce Web Application",
      category: "web",
      description: "Full-stack e-commerce solution with React frontend and Node.js backend. Features include payment integration, inventory management, and real-time order tracking.",
      image: "/images/ecommerce-3562191_1280 ecommerce.jpg",
      technologies: ["React", "Node.js", "PostgreSQL", "Express"],
      link: "#",
      featured: true
    },
    {
      id: 2,
      title: "Business Network Setup",
      category: "networking",
      description: "Complete office network infrastructure with secure wireless access, VLAN segmentation, and enterprise-grade firewall protection.",
      image: "/images/managed cabinet.jpg",
      technologies: ["Cisco", "Firewall", "Wi-Fi", "Security"],
      link: "#"
    },
    {
      id: 3,
      title: "System Automation Scripts",
      category: "automation",
      description: "PowerShell automation for system deployment and maintenance, reducing manual tasks by 70% and improving operational efficiency.",
      image: "/images/db automation.jpg",
      technologies: ["PowerShell", "Windows", "Automation"],
      link: "#"
    },
    {
      id: 4,
      title: "Corporate Website Redesign",
      category: "web",
      description: "Modern responsive website for corporate client with headless CMS integration, improved SEO, and 200% faster load times.",
      image: "/images/web design001.jpg",
      technologies: ["React", "Contentful", "CSS3"],
      link: "#"
    },
    {
      id: 5,
      title: "IT Support Portal",
      category: "web",
      description: "Internal support ticket system for IT service management with automated routing, SLA tracking, and real-time analytics.",
      image: "/images/pexels-divinetechygirl-1181271.jpg",
      technologies: ["React", "Express", "MongoDB"],
      link: "#"
    },
    {
      id: 6,
      title: "Network Security Audit",
      category: "security",
      description: "Comprehensive security assessment and hardening for business network. Identified and resolved 15+ critical vulnerabilities.",
      image: "/images/SECURE NET.jpg",
      technologies: ["Security", "Firewall", "Monitoring"],
      link: "#"
    },
    {
      id: 7,
      title: "Mobile App Development",
      category: "web",
      description: "Cross-platform mobile application for business productivity with offline sync, push notifications, and real-time collaboration features.",
      image: "/images/Mobile App Development.png",
      technologies: ["React Native", "Firebase", "iOS", "Android"],
      link: "#"
    },
    {
      id: 8,
      title: "SecureLogix - Gate Management",
      category: "automation",
      description: "Standalone gate management system with secure access control, real-time monitoring, automated operations, and comprehensive reporting dashboard.",
      image: "/images/logix-gate-system.png",
      technologies: ["React", "Node.js", "Express", "PostgreSQL"],
      link: "#",
      featured: true
    },
    {
      id: 9,
      title: "Tally Prime + eTIMS Integration",
      category: "Accounting Automation",
      description: "Professional setup and support for Tally Prime with seamless eTIMS integration for compliant invoicing, automated reporting, and streamlined financial operations.",
      image: "/images/tally-etims-support.png",
      technologies: ["Tally Prime", "eTIMS", "Windows Server", "LAN Setup", "Data Migration"],
      link: "#"
    }
  ];

  const categories = [
    { id: 'all', name: 'All Projects' },
    { id: 'web', name: 'Web Development' },
    { id: 'networking', name: 'Networking' },
    { id: 'automation', name: 'Automation' },
    { id: 'security', name: 'Security' },
    { id: 'Accounting Automation', name: 'Accounting Automation' }
  ];

  const filteredProjects = activeFilter === 'all' 
    ? projects 
    : projects.filter(project => project.category === activeFilter);

  // Count projects by category for structured data
  const projectCount = projects.length;

  // Structured Data for Portfolio
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Raffcodes Portfolio - IT Solutions & Development Projects",
    "description": "Explore our portfolio of IT solutions, web development, networking, automation, and security projects in Kenya.",
    "url": "https://raffcodes.tech/portfolio",
    "about": {
      "@type": "Organization",
      "name": "Raffcodes",
      "description": "IT solutions provider specializing in full-stack development, networking, automation, and security."
    },
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": projects.map((project, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "CreativeWork",
          "name": project.title,
          "description": project.description,
          "url": `https://raffcodes.tech/portfolio#project-${project.id}`,
          "applicationCategory": project.category
        }
      }))
    }
  };

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Portfolio | Raffcodes - IT Solutions & Development Projects</title>
        <meta name="description" content="Explore Raffcodes' portfolio of IT solutions including web development, networking, automation, security, and Tally Prime eTIMS integration projects in Nairobi, Kenya." />
        <meta name="keywords" content="IT portfolio Nairobi, web development projects Kenya, networking solutions, automation scripts, security audits, Tally Prime eTIMS integration, Raffcodes projects" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://raffcodes.tech/portfolio" />

        {/* Open Graph Tags */}
        <meta property="og:title" content="Portfolio | Raffcodes - IT Solutions & Development Projects" />
        <meta property="og:description" content="Explore our portfolio of IT solutions including web development, networking, automation, security, and Tally Prime eTIMS integration projects." />
        <meta property="og:url" content="https://raffcodes.tech/portfolio" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://raffcodes.tech/images/portfolio-og.jpg" />
        <meta property="og:site_name" content="Raffcodes" />
        <meta property="og:locale" content="en_KE" />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Portfolio | Raffcodes - IT Solutions & Development Projects" />
        <meta name="twitter:description" content="Explore our portfolio of IT solutions and development projects in Kenya." />
        <meta name="twitter:image" content="https://raffcodes.tech/images/portfolio-og.jpg" />
        <meta name="twitter:site" content="@raffcodes" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="page portfolio">
        {/* Portfolio Header - Enhanced */}
        <section className="portfolio-header">
          <div className="container">
            <div className="portfolio-header-content">
              <span className="header-tag">Our Work</span>
              <h1>Portfolio</h1>
              <p className="header-subtitle">
                Explore our latest projects and see how we've helped clients transform their technology
              </p>
              <div className="header-stats">
                <div className="stat-item">
                  <span className="stat-number">{projectCount}+</span>
                  <span className="stat-label">Projects Delivered</span>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-item">
                  <span className="stat-number">4+</span>
                  <span className="stat-label">Categories</span>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-item">
                  <span className="stat-number">100%</span>
                  <span className="stat-label">Client Satisfaction</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Filter Buttons - Enhanced */}
        <section className="portfolio-filters">
          <div className="container">
            <div className="filter-wrapper">
              <div className="filter-buttons">
                {categories.map(category => (
                  <button
                    key={category.id}
                    className={`filter-btn ${activeFilter === category.id ? 'active' : ''}`}
                    onClick={() => setActiveFilter(category.id)}
                  >
                    {category.name}
                    {activeFilter === category.id && (
                      <span className="filter-indicator"></span>
                    )}
                  </button>
                ))}
              </div>
              <div className="filter-results">
                <span>{filteredProjects.length} projects found</span>
              </div>
            </div>
          </div>
        </section>

        {/* Projects Grid - Enhanced */}
        <section className="portfolio-grid">
          <div className="container">
            <div className="projects-container">
              {filteredProjects.length > 0 ? (
                filteredProjects.map(project => (
                  <div key={project.id} className={`project-card ${project.featured ? 'featured' : ''}`}>
                    {project.featured && (
                      <div className="featured-badge">
                        <i className="fas fa-star"></i> Featured
                      </div>
                    )}
                    <div className="project-image">
                      <img 
                        src={project.image} 
                        alt={`${project.title} - Raffcodes portfolio project`}
                        loading="lazy"
                        onError={(e) => {
                          e.target.src = "/images/raffcodes-logo.png";
                          e.target.alt = "Raffcodes - IT Solutions";
                        }}
                      />
                      <div className="project-overlay">
                        <div className="overlay-content">
                          <div className="project-category">{project.category}</div>
                          <a href={project.link} className="view-project">
                            <i className="fas fa-eye"></i> View Project
                          </a>
                        </div>
                      </div>
                    </div>
                    <div className="project-info">
                      <h3>{project.title}</h3>
                      <p>{project.description}</p>
                      <div className="project-technologies">
                        {project.technologies.map((tech, index) => (
                          <span key={index} className="tech-tag">{tech}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-projects">
                  <i className="fas fa-search"></i>
                  <h3>No projects found</h3>
                  <p>Try selecting a different category</p>
                  <button 
                    className="filter-btn active"
                    onClick={() => setActiveFilter('all')}
                  >
                    View All Projects
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Call to Action - Enhanced */}
        <section className="portfolio-cta">
          <div className="container">
            <div className="cta-content">
              <div className="cta-text">
                <h2>Ready to Start Your Project?</h2>
                <p>Let's discuss how we can bring your ideas to life with the right technology solutions</p>
              </div>
              <div className="cta-actions">
                <a href="/contact" className="cta primary">
                  <i className="fas fa-rocket"></i> Get Started
                </a>
                <a href="/services" className="cta secondary">
                  <i className="fas fa-code"></i> View Services
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}